/*
 * Conversion King slide cart — ToolBox Beauty (Broadcast theme).
 *
 * Ported from PROLON-LIFE-HORIZON/assets/conversion-king-slide-cart.js: same
 * Vue 3 app shape (a reactive `store`, `slideCartState` / `toggleCart` for the
 * drawer, an in-DOM template inside #cart-with-header using ${ } delimiters,
 * the focus trap). The Prolon-only parts are gone: HCP / Stripe checkout,
 * free gift rules, promo gift bars, subscriptions, expedited shipping,
 * lean-cart A/B variants, analytics, axios, jQuery, js-cookie, vue3-carousel.
 *
 * What it adds for ToolBox:
 *   - "Complete the set" box under a single brush: the one set it upgrades
 *     to, with the price difference on the button. Clicking it adds the set
 *     (quantity 1) and takes out one unit of every brush the set contains.
 *   - "Complete your toolbox." recommendation row, also on the empty cart.
 *   - Free shipping bar; it stays, full, with its own message once unlocked.
 *   - Discount codes through the Shopify cart API, with a line per discount.
 *   - A quiet country/currency selector under checkout (Shopify Markets
 *     localization form); the drawer reopens after the page reloads.
 *   - A blurred backdrop where the mouse pointer turns into a round close
 *     button, and arrows for the recommendation row on desktop.
 * The upgrade offers, bundle contents and recommendations come from
 * templates/cart.slidecart.liquid, which reads the offers from the Cart offer
 * metaobjects (Content > Metaobjects in the admin); the cart itself from
 * /cart.js; what each set holds from the Storefront API (see setContents).
 *
 * Broadcast keeps doing every add-to-cart (form validation, errors, quick add).
 * The cart only takes over opening: an inline script in the snippet points the
 * theme drawer's openCartDrawer() and the header cart links ([data-cart-toggle])
 * at window.KingSlideCart.open(), before the theme JS runs.
 *
 * Markup: snippets/king-slide-cart.liquid. Settings: Theme settings > Slide cart.
 */
(function () {
  "use strict"

  const mountEl = document.getElementById("cart-with-header")
  const configEl = document.getElementById("king-slide-cart-config")
  if (!mountEl || !configEl || !window.Vue) return

  let config
  try {
    config = JSON.parse(configEl.textContent)
  } catch (error) {
    console.error("[slide-cart] Invalid config", error)
    return
  }

  const routes = config.routes
  const url = {
    cart: routes.cart + ".js",
    view: routes.cart + "?view=slidecart",
    add: routes.cart_add + ".js",
    change: routes.cart_change + ".js",
    update: routes.cart_update + ".js",
  }

  /* ---------------------------------------------------------------- helpers */

  // JSON request against the Shopify cart API. Throws with Shopify's own
  // message (e.g. "You can only add 3 of this item") on a non-2xx response.
  async function request(endpoint, body) {
    const options = body === undefined
      ? { headers: { Accept: "application/json" } }
      : {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(body),
        }
    const response = await fetch(endpoint, options)
    let data = null
    try {
      data = await response.json()
    } catch (error) {}
    if (!response.ok) {
      const message = (data && (data.description || data.message)) || config.strings.error
      const failure = new Error(message)
      failure.status = response.status
      throw failure
    }
    return data
  }

  // The slidecart view is HTML-served JSON; a theme error page must not break the cart.
  // No "Accept: application/json" here: with it Shopify answers /cart with the
  // plain cart JSON and ignores ?view=.
  async function requestExtras() {
    try {
      const response = await fetch(url.view)
      if (!response.ok) return null
      return JSON.parse(await response.text())
    } catch (error) {
      console.error("[slide-cart] Could not read cart?view=slidecart", error)
      return null
    }
  }

  function sizedImage(src, width) {
    if (!src) return ""
    return src + (src.indexOf("?") === -1 ? "?" : "&") + "width=" + width
  }

  function formatMoney(cents) {
    const value = Number(cents) || 0
    const theme = window.theme
    if (theme && typeof theme.formatMoney === "function" && theme.moneyFormat) {
      return theme.formatMoney(value, theme.moneyFormat)
    }
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD"
    return new Intl.NumberFormat(document.documentElement.lang || "en", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    }).format(value / 100)
  }

  // A pick from the Rebuy bundle builder. Its discount depends on the whole
  // build, so an upgrade must never remove it.
  function isBundleBuilderLine(line) {
    return !!(line.properties && line.properties._r_added_bundle_session_id)
  }

  // An add-on bought with a set (the PR box), tagged `_addon_for: <set variant
  // id>` by assets/section-pdp-set.js. It shows under that set, not as its own item.
  function addonParent(line) {
    return Number((line.properties && line.properties._addon_for) || 0)
  }

  // Switching country posts Shopify's localization form, which reloads the
  // page in the new currency. This flag reopens the drawer after that reload;
  // it expires quickly so a stale one never pops the cart open later.
  const REOPEN_KEY = "king-slide-cart:reopen"

  function rememberReopen() {
    try {
      sessionStorage.setItem(REOPEN_KEY, String(Date.now()))
    } catch (error) {}
  }

  function takeReopen() {
    try {
      const at = Number(sessionStorage.getItem(REOPEN_KEY))
      sessionStorage.removeItem(REOPEN_KEY)
      return at > 0 && Date.now() - at < 30000
    } catch (error) {
      return false
    }
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    })[char])
  }

  /* --------------------------------------------------------- complete the set */

  const upgradeSettings = config.upgrade || {}
  const upgradeDiscount = Math.min(Math.max(Number(upgradeSettings.discountPercent) || 0, 0), 100) / 100

  // What each set holds, by set variant id: [{ variant_id, quantity }]. Liquid
  // only knows a bundle's contents once it is in the cart, and the box needs
  // them before: brush count, which lines the set replaces, the price
  // difference. They come from the Storefront API (a read-only product query,
  // open on the online store without a token) and are kept for the session.
  // null means they could not be read; the box then treats the set as
  // replacing the clicked brush only.
  const SET_CONTENTS_KEY = "king-slide-cart:set-contents"
  const SET_CONTENTS_QUERY =
    "query SetContents($ids: [ID!]!) { nodes(ids: $ids) { ... on ProductVariant { id components(first: 30) { nodes { quantity productVariant { id } } } } } }"
  const setContents = Vue.reactive(readStoredContents())
  const loadingContents = new Set()

  function readStoredContents() {
    try {
      return JSON.parse(sessionStorage.getItem(SET_CONTENTS_KEY)) || {}
    } catch (error) {
      return {}
    }
  }

  function gidNumber(gid) {
    return Number(String(gid).split("/").pop())
  }

  async function loadSetContents(variantIds) {
    const ids = variantIds.filter((id) => !(id in setContents) && !loadingContents.has(id))
    if (!ids.length) return
    ids.forEach((id) => loadingContents.add(id))
    let nodes = null
    try {
      const response = await fetch(upgradeSettings.storefrontApi, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          query: SET_CONTENTS_QUERY,
          variables: { ids: ids.map((id) => "gid://shopify/ProductVariant/" + id) },
        }),
      })
      const data = response.ok ? await response.json() : null
      nodes = (data && data.data && data.data.nodes) || null
    } catch (error) {
      console.error("[slide-cart] Could not read set contents", error)
    }
    ids.forEach((id) => {
      const node = (nodes || []).find((entry) => entry && gidNumber(entry.id) === id)
      const parts = (node && node.components && node.components.nodes) || []
      setContents[id] = parts.length
        ? parts.map((part) => ({ variant_id: gidNumber(part.productVariant.id), quantity: part.quantity }))
        : null
      loadingContents.delete(id)
    })
    try {
      const known = {}
      Object.keys(setContents).forEach((id) => {
        if (setContents[id]) known[id] = setContents[id]
      })
      sessionStorage.setItem(SET_CONTENTS_KEY, JSON.stringify(known))
    } catch (error) {}
  }

  // The cart units a set replaces: for each brush in it, as many units as the
  // set holds (one each here), taken from paid single lines. Free lines, Rebuy
  // bundle builder picks and other sets are never touched. `updates` is the
  // /cart/update.js body, `value` what those units cost now after their own
  // discounts, `missing` how many of the set's brushes the cart doesn't have.
  function replacementPlan(items, contents) {
    const needed = new Map()
    contents.forEach((part) =>
      needed.set(part.variant_id, (needed.get(part.variant_id) || 0) + part.quantity)
    )
    const updates = {}
    let value = 0
    items.forEach((line) => {
      const want = needed.get(line.variant_id) || 0
      if (!want || line.has_components || isBundleBuilderLine(line) || line.final_line_price === 0) return
      const take = Math.min(want, line.quantity)
      needed.set(line.variant_id, want - take)
      updates[line.key] = line.quantity - take
      value += take === line.quantity
        ? line.final_line_price
        : Math.round((line.final_line_price * take) / line.quantity)
    })
    let missing = 0
    needed.forEach((count) => (missing += count))
    return { updates, value, missing }
  }

  // Percentage discounts on every product (target "all"), which would reach
  // the set as well. Discounts limited to some products, minimum spends and
  // buy X get Y can't be told for a set that isn't in the cart yet; those are
  // left out, so the button can be off by them until the set is added.
  function sitewidePercentages(applications) {
    const found = new Map()
    applications.forEach((application) => {
      if (!application || application.value_type !== "percentage" || application.target_selection !== "all") return
      if (application.target_type && application.target_type !== "line_item") return
      const value = parseFloat(application.value)
      if (value > 0 && value <= 100) found.set(application.key || application.title, value / 100)
    })
    return Array.from(found.values())
  }

  // How the cart's merchandise total moves when the set replaces `plan`, in
  // the cart's currency (cents). Product discounts on everything come off the
  // set's price, then the upgrade discount from the theme settings; order
  // discounts on everything come off the whole difference.
  function upgradePricing(cart, setPrice, plan) {
    const lineApplications = []
    cart.items.forEach((line) =>
      (line.line_level_discount_allocations || []).forEach((allocation) =>
        lineApplications.push(allocation.discount_application)
      )
    )
    let set = setPrice
    sitewidePercentages(lineApplications).forEach((percentage) => (set *= 1 - percentage))
    const savings = set * upgradeDiscount
    let difference = set - savings - plan.value
    sitewidePercentages(cart.cart_level_discount_applications || []).forEach(
      (percentage) => (difference *= 1 - percentage)
    )
    return { difference: Math.round(difference), savings: Math.round(savings) }
  }

  /* ------------------------------------------------------------------ store */

  const store = Vue.reactive({
    state: {
      cart: null,
      extras: { lines: {}, recommendations: [] },
      loadingCart: true,
      updatingCart: false,
      error: "",
      announcement: "",
    },

    // Mutations run one at a time, so fast +/+/+ clicks or an upgrade racing a
    // quantity change can never interleave on the cart.
    _queue: Promise.resolve(),
    enqueue(task) {
      const run = this._queue.then(task, task)
      this._queue = run.catch(() => {})
      return run
    },

    // /cart.js and the slidecart view go out together and land together, so the
    // line items and their upgrade offers / bundle contents always match.
    _refreshing: null,
    _refreshAgain: false,
    getCart() {
      if (this._refreshing) {
        this._refreshAgain = true
        return this._refreshing
      }
      this._refreshing = (async () => {
        try {
          do {
            this._refreshAgain = false
            const [cart, extras] = await Promise.all([request(url.cart), requestExtras()])
            this.setCart(cart)
            if (extras) {
              this.state.extras = extras
              loadSetContents(
                Object.values(extras.lines || {})
                  .map((line) => line.upgrade && line.upgrade.variant_id)
                  .filter(Boolean)
              )
            }
          } while (this._refreshAgain)
        } catch (error) {
          this.showError(error.message)
        } finally {
          this.state.loadingCart = false
          this._refreshing = null
        }
        return this.state.cart
      })()
      return this._refreshing
    },

    setCart(cart) {
      if (!cart || !Array.isArray(cart.items)) return
      this.state.cart = cart
      // Broadcast's header <cart-count> listens for this.
      document.dispatchEvent(
        new CustomEvent("theme:cart:change", {
          bubbles: true,
          detail: { cartCount: cart.item_count },
        })
      )
    },

    // Every write goes through here: queue, flag the cart busy, then re-read
    // cart + view. A failure is shown in the drawer and the cart is re-read, so
    // the UI never keeps an optimistic value Shopify rejected. The busy flag is
    // a count, so it only clears once the last queued write has finished.
    _pendingWrites: 0,
    // Set once the drawer has written to the cart (see toggleClose).
    changed: false,
    async mutate(task) {
      this._pendingWrites += 1
      this.state.updatingCart = true
      try {
        return await this.enqueue(task)
      } catch (error) {
        this.showError(error.message)
        throw error
      } finally {
        this.changed = true
        await this.getCart()
        this._pendingWrites -= 1
        this.state.updatingCart = this._pendingWrites > 0
      }
    },

    addToCart(variantId, quantity) {
      return this.mutate(() =>
        request(url.add, { items: [{ id: variantId, quantity: quantity || 1 }] })
      )
    },

    changeLine(key, quantity) {
      return this.mutate(() => request(url.change, { id: key, quantity }))
    },

    setDiscountCodes(codes) {
      return request(url.update, { discount: codes.join(",") })
    },

    _errorTimer: null,
    showError(message) {
      this.state.error = message || config.strings.error
      clearTimeout(this._errorTimer)
      this._errorTimer = setTimeout(() => {
        this.state.error = ""
      }, 6000)
      this.state.announcement = this.state.error
    },
  })

  /* ----------------------------------------------------------------- drawer */

  const slideCartState = Vue.reactive({
    hidden: true,
  })
  // The element that had focus when the drawer opened; focus goes back to it on close.
  let slideCartOpener = null
  // Focus trap for the open drawer. First/last are looked up on every key press,
  // because the rows, upsells and footer render from cart data after it opens.
  const slideCartRoot = () =>
    document.querySelector('#cart-with-header [role="dialog"][aria-modal="true"]')
  const slideCartFocusable = (root) =>
    Array.from(
      root.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    ).filter((el) => el.offsetWidth || el.offsetHeight || el.getClientRects().length)
  const slideCartTrap = {
    keydown(event) {
      if (event.key !== "Tab") return
      const root = slideCartRoot()
      if (!root) return
      const focusable = slideCartFocusable(root)
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && (active === first || active === root)) {
        event.preventDefault()
        last.focus()
      }
    },
    focusin(event) {
      const root = slideCartRoot()
      if (root && event.target instanceof Node && !root.contains(event.target)) {
        event.stopPropagation()
        ;(slideCartFocusable(root)[0] || root).focus()
      }
    },
  }

  const toggleCart = {
    showCart() {
      if (slideCartState.hidden) {
        slideCartState.hidden = false
        document.body.classList.add("slide-cart-open")
        document.dispatchEvent(new CustomEvent("theme:scroll:lock", { bubbles: true }))
      }
      return store.getCart()
    },
    openCart() {
      if (slideCartState.hidden) {
        return this.showCart()
      }
      toggleClose.closeAll()
    },
  }

  const toggleClose = {
    closeAll() {
      if (slideCartState.hidden) return
      slideCartState.hidden = true
      document.body.classList.remove("slide-cart-open")
      document.dispatchEvent(new CustomEvent("theme:scroll:unlock", { bubbles: true }))
      // The cart page renders its lines server side; show it the drawer's changes.
      if (config.isCartPage && store.changed) window.location.reload()
    },
  }

  /* -------------------------------------------------------------------- app */

  Vue.createApp({
    delimiters: ["${", "}"],
    data() {
      return {
        discountCode: "",
        addingDiscount: false,
        checkoutLoading: false,
        switchingCountry: false,
        // The discount code field sits behind "Add a discount code".
        discountOpen: false,
        // Recommendation row arrows (desktop): shown when the row overflows,
        // each disabled at its end.
        recsOverflow: false,
        recsAtStart: true,
        recsAtEnd: false,
        // Per-line UI state, keyed by line key: "plus" | "minus" | "remove" | "upgrade".
        busyLines: {},
        // Bundle rows whose contents are expanded.
        openBundles: {},
        // Recommendation variant ids being added.
        addingRecommendations: {},
      }
    },
    computed: {
      cart() {
        return store.state.cart
      },
      items() {
        return (this.cart && this.cart.items) || []
      },
      // Add-on lines by the key of the set line they show under: the first line
      // of their set's variant. One whose set is gone stays a line of its own.
      addonLines() {
        const byKey = {}
        this.items.forEach((line) => {
          const parentId = addonParent(line)
          if (!parentId) return
          const parent = this.items.find((item) => item.variant_id === parentId && !addonParent(item))
          if (!parent) return
          if (!byKey[parent.key]) byKey[parent.key] = []
          byKey[parent.key].push(line)
        })
        return byKey
      },
      lineItems() {
        const nested = new Set()
        Object.values(this.addonLines).forEach((lines) => lines.forEach((line) => nested.add(line.key)))
        return this.items.filter((item) => !nested.has(item.key))
      },
      hasItems() {
        return this.items.length > 0
      },
      cartCount() {
        return (this.cart && this.cart.item_count) || 0
      },
      cartTotal() {
        return (this.cart && this.cart.total_price) || 0
      },
      original_total_price() {
        return (this.cart && this.cart.original_total_price) || 0
      },
      loadingCart() {
        return store.state.loadingCart
      },
      disableCart() {
        return store.state.updatingCart
      },
      cartError() {
        return store.state.error
      },
      liveMessage() {
        return store.state.announcement
      },
      isvisibleCart() {
        return !slideCartState.hidden
      },
      cartProductIds() {
        return new Set(this.items.map((item) => item.product_id))
      },
      freeShipping() {
        const settings = config.freeShipping
        const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1
        const threshold = Math.round(settings.limit * 100 * rate)
        if (!settings.enabled || !threshold) return null
        const remaining = Math.max(threshold - this.cartTotal, 0)
        return {
          unlocked: remaining === 0,
          remaining,
          percent: Math.min((this.cartTotal / threshold) * 100, 100),
        }
      },
      freeShippingMessage() {
        if (!this.freeShipping) return ""
        if (this.freeShipping.unlocked) return config.freeShipping.unlockedMessage
        const amount = "<strong>" + escapeHtml(formatMoney(this.freeShipping.remaining)) + "</strong>"
        return config.freeShipping.message.replace("||amount||", amount)
      },
      // One row per discount (code or automatic), summed across cart-level and
      // line-level allocations, so "WELCOME15 -$5.25" reads like it does at checkout.
      discountLines() {
        if (!this.cart) return []
        const lines = new Map()
        const add = (title, amount, type) => {
          if (!title || !amount) return
          const entry = lines.get(title) || { title, amount: 0, isCode: false }
          entry.amount += amount
          if (type === "discount_code") entry.isCode = true
          lines.set(title, entry)
        }
        ;(this.cart.cart_level_discount_applications || []).forEach((application) =>
          add(application.title, application.total_allocated_amount, application.type)
        )
        this.items.forEach((item) =>
          (item.line_level_discount_allocations || []).forEach((allocation) =>
            add(
              allocation.discount_application && allocation.discount_application.title,
              allocation.amount,
              allocation.discount_application && allocation.discount_application.type
            )
          )
        )
        const codes = this.appliedCodes.map((code) => code.code.toLowerCase())
        return Array.from(lines.values()).map((line) => ({
          ...line,
          isCode: line.isCode || codes.indexOf(line.title.toLowerCase()) !== -1,
        }))
      },
      appliedCodes() {
        return (this.cart && this.cart.discount_codes) || []
      },
      // Codes that are on the cart but give nothing (e.g. entered through a
      // /discount/CODE link). Shown so the shopper can see and remove them.
      inactiveCodes() {
        const active = this.discountLines.map((line) => line.title.toLowerCase())
        return this.appliedCodes.filter(
          (code) => !code.applicable && active.indexOf(code.code.toLowerCase()) === -1
        )
      },
      // Never repeat a set that an upgrade box offers (or is about to, while
      // its contents load).
      recommendations() {
        const list = (store.state.extras && store.state.extras.recommendations) || []
        const offered = new Set(
          this.items
            .map((item) => (this.lineExtras(item) || {}).upgrade)
            .filter(Boolean)
            .map((offer) => offer.product_id)
        )
        return list.filter(
          (product) => !this.cartProductIds.has(product.product_id) && !offered.has(product.product_id)
        )
      },
      // One "Complete the set" box per set, under the first line in the cart
      // that upgrades to it, checked against the live cart: none while the
      // set is in the cart, once the cart holds every brush in it, or before
      // its contents have loaded (that takes one request, then it's cached).
      upgradeOffers() {
        const offers = {}
        if (!this.cart) return offers
        const claimed = new Set()
        this.items.forEach((item) => {
          const extras = this.lineExtras(item)
          const offer = extras && extras.upgrade
          if (!offer || claimed.has(offer.variant_id) || this.cartProductIds.has(offer.product_id)) return
          if (!(offer.variant_id in setContents)) return
          const known = setContents[offer.variant_id]
          if (known && !known.some((part) => part.variant_id === item.variant_id)) return
          const plan = replacementPlan(this.items, known || [{ variant_id: item.variant_id, quantity: 1 }])
          if (!(item.key in plan.updates) || (known && !plan.missing)) return
          claimed.add(offer.variant_id)
          offers[item.key] = {
            ...offer,
            contents: known,
            count: known ? known.reduce((total, part) => total + part.quantity, 0) : 0,
            ...upgradePricing(this.cart, offer.price, plan),
          }
        })
        return offers
      },
      upgradeLabel() {
        return upgradeSettings.label || "Complete the set"
      },
      emptyLink() {
        return config.emptyLink
      },
    },
    methods: {
      money(cents) {
        return formatMoney(cents)
      },
      // "+$70.00", or "−$14.40" when the set costs less than what it replaces.
      signedMoney(cents) {
        return cents < 0 ? "\u2212" + formatMoney(-cents) : "+" + formatMoney(cents)
      },
      upgradeSavings(offer) {
        const label = upgradeSettings.discountLabel
        if (!label || !upgradeDiscount || offer.savings <= 0) return ""
        return label.replace("||amount||", formatMoney(offer.savings))
      },
      image(src, width) {
        return sizedImage(src, width)
      },
      closeAllDrawers() {
        toggleClose.closeAll()
      },
      // Over the backdrop the pointer becomes a round close button. The dot is
      // moved straight from the event (no reactive state), so it never lags.
      // Mouse only: on touch the backdrop is a plain tap to close.
      moveCursor(event) {
        const cursor = this.$refs.cursor
        if (!cursor || event.pointerType !== "mouse") return
        cursor.style.transform = "translate3d(" + event.clientX + "px, " + event.clientY + "px, 0)"
        cursor.classList.add("is-visible")
      },
      hideCursor() {
        const cursor = this.$refs.cursor
        if (cursor) cursor.classList.remove("is-visible")
      },
      toggleDiscount() {
        this.discountOpen = !this.discountOpen
        if (this.discountOpen) {
          this.$nextTick(() => this.$refs.discountInput && this.$refs.discountInput.focus())
        }
      },
      updateRecsEdges() {
        const track = this.$refs.recsTrack
        if (!track) return
        const max = track.scrollWidth - track.clientWidth
        this.recsOverflow = max > 1
        this.recsAtStart = track.scrollLeft <= 1
        this.recsAtEnd = track.scrollLeft >= max - 1
      },
      scrollRecs(direction) {
        const track = this.$refs.recsTrack
        if (!track) return
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        track.scrollBy({ left: direction * track.clientWidth * 0.9, behavior: reduce ? "auto" : "smooth" })
      },
      lineExtras(item) {
        const lines = (store.state.extras && store.state.extras.lines) || {}
        return lines[item.key] || null
      },
      bundleComponents(item) {
        const extras = this.lineExtras(item)
        return (extras && extras.components) || []
      },
      bundleCount(item) {
        return this.bundleComponents(item).reduce((total, component) => total + component.quantity, 0)
      },
      toggleBundle(item) {
        this.openBundles[item.key] = !this.openBundles[item.key]
      },
      visibleProperties(item) {
        const properties = item.properties || {}
        return Object.keys(properties)
          .filter((key) => key.charAt(0) !== "_" && properties[key] !== "" && properties[key] !== null)
          .map((key) => ({ key, value: properties[key] }))
      },
      isFree(item) {
        return item.final_line_price === 0 && item.original_line_price > 0
      },
      upgradeFor(item) {
        return this.upgradeOffers[item.key] || null
      },
      isBusy(item, action) {
        return action ? this.busyLines[item.key] === action : !!this.busyLines[item.key]
      },
      async runLine(item, action, task) {
        if (this.busyLines[item.key]) return
        this.busyLines[item.key] = action
        try {
          await task()
        } catch (error) {
          // store.mutate already showed the message and re-read the cart.
        } finally {
          delete this.busyLines[item.key]
        }
      },
      increment(item) {
        return this.runLine(item, "plus", () => store.changeLine(item.key, item.quantity + 1))
      },
      decrement(item) {
        if (item.quantity <= 1) return
        return this.runLine(item, "minus", () => store.changeLine(item.key, item.quantity - 1))
      },
      addonsFor(item) {
        return this.addonLines[item.key] || []
      },
      // Removing a set takes its add-ons with it, unless another line of the
      // same set stays (they move under that one).
      removeItem(item) {
        const others = this.items.some(
          (line) => line.key !== item.key && line.variant_id === item.variant_id && !addonParent(line)
        )
        const addons = others ? [] : this.addonsFor(item)
        if (!addons.length) return this.runLine(item, "remove", () => store.changeLine(item.key, 0))
        const updates = { [item.key]: 0 }
        addons.forEach((line) => (updates[line.key] = 0))
        return this.runLine(item, "remove", () => store.mutate(() => request(url.update, { updates })))
      },
      // Replace, not add: add one of the set, then take out one unit of every
      // brush it holds (replacementPlan), worked out again on the cart as it
      // is after the add. If the set's contents could not be read beforehand,
      // they come from the new set line (line_item.item_components); failing
      // that, only the clicked brush goes. The upgrade discount code, if the
      // theme settings have one, goes on in the same request; a code Shopify
      // won't apply comes off again quietly, since the set did go in.
      performUpgrade(item) {
        const offer = this.upgradeFor(item)
        if (!offer) return
        const code = (upgradeSettings.discountCode || "").trim()
        return this.runLine(item, "upgrade", () =>
          store.mutate(async () => {
            await request(url.add, { items: [{ id: offer.variant_id, quantity: 1 }] })

            const [cart, extras] = await Promise.all([request(url.cart), requestExtras()])
            let contents = offer.contents
            if (!contents) {
              const lineExtras = (extras && extras.lines) || {}
              const setLine = cart.items.find(
                (line) => line.variant_id === offer.variant_id && line.has_components && line.quantity === 1
              )
              const parts = setLine && lineExtras[setLine.key] && lineExtras[setLine.key].components
              contents = parts && parts.length ? parts : [{ variant_id: item.variant_id, quantity: 1 }]
            }

            const body = {}
            const updates = replacementPlan(cart.items, contents).updates
            if (Object.keys(updates).length) body.updates = updates
            const codes = (cart.discount_codes || []).map((entry) => entry.code)
            const addCode = !!code && !codes.some((entry) => entry.toLowerCase() === code.toLowerCase())
            if (addCode) body.discount = codes.concat(code).join(",")
            if (!Object.keys(body).length) return

            const updated = await request(url.update, body)
            if (addCode) {
              const applied = ((updated && updated.discount_codes) || []).find(
                (entry) => entry.code.toLowerCase() === code.toLowerCase()
              )
              if (!applied || !applied.applicable) await store.setDiscountCodes(codes)
            }
          })
        )
      },
      async addRecommendation(product) {
        if (this.addingRecommendations[product.variant_id]) return
        this.addingRecommendations[product.variant_id] = true
        try {
          await store.addToCart(product.variant_id, 1)
        } catch (error) {
        } finally {
          delete this.addingRecommendations[product.variant_id]
        }
      },
      async applyDiscount() {
        const code = this.discountCode.trim()
        if (!code || this.addingDiscount) return
        const existing = this.appliedCodes.map((entry) => entry.code)
        if (existing.some((entry) => entry.toLowerCase() === code.toLowerCase())) {
          this.discountCode = ""
          return
        }
        this.addingDiscount = true
        try {
          await store.mutate(async () => {
            const cart = await store.setDiscountCodes(existing.concat(code))
            const applied = (cart.discount_codes || []).find(
              (entry) => entry.code.toLowerCase() === code.toLowerCase()
            )
            if (!applied || !applied.applicable) {
              // Put the cart back the way it was before showing the error.
              await store.setDiscountCodes(existing)
              throw new Error(config.strings.invalidCode.replace("||code||", code))
            }
          })
          this.discountCode = ""
          store.state.announcement = config.strings.codeApplied.replace("||code||", code)
        } catch (error) {
        } finally {
          this.addingDiscount = false
        }
      },
      async removeDiscount(code) {
        const remaining = this.appliedCodes
          .map((entry) => entry.code)
          .filter((entry) => entry.toLowerCase() !== code.toLowerCase())
        try {
          await store.mutate(() => store.setDiscountCodes(remaining))
        } catch (error) {}
      },
      dismissError() {
        store.state.error = ""
      },
      openCheckout() {
        if (this.checkoutLoading || !this.hasItems) return
        this.checkoutLoading = true
        window.location.href = config.checkoutUrl
        // Back/forward cache can restore the page mid-redirect.
        setTimeout(() => {
          this.checkoutLoading = false
        }, 8000)
      },
      // Country/currency selector in the footer (Shopify's localization form).
      // Shopify reprices the cart and sends the shopper back to this page.
      changeCountry(event) {
        const form = event.target.form
        if (!form || this.switchingCountry) return
        rememberReopen()
        form.submit()
        this.switchingCountry = true
        // A stopped or failed navigation must not leave the selector locked.
        setTimeout(() => {
          this.switchingCountry = false
        }, 8000)
      },
    },
    created() {
      store.getCart()
      document.addEventListener("keyup", (event) => {
        if (event.key !== "Escape" || slideCartState.hidden) return
        this.closeAllDrawers()
      })
      window.addEventListener("resize", () => this.updateRecsEdges())
      window.addEventListener("pageshow", (event) => {
        if (event.persisted) {
          this.checkoutLoading = false
          // Back from a country switch: the page still shows the old country,
          // so put the selector back on it too.
          if (this.switchingCountry) {
            this.switchingCountry = false
            const select = document.getElementById("kc-currency-select")
            if (select && select.form) select.form.reset()
          }
          store.getCart()
        }
      })
    },
    watch: {
      recommendations() {
        this.$nextTick(() => this.updateRecsEdges())
      },
      // Say it out loud when a change in the open drawer unlocks free shipping;
      // the bar text itself is not a live region.
      "freeShipping.unlocked"(unlocked, wasUnlocked) {
        if (!unlocked || wasUnlocked !== false || !this.isvisibleCart) return
        const message = new DOMParser().parseFromString(config.freeShipping.unlockedMessage, "text/html")
        store.state.announcement = message.body.textContent.trim()
      },
      isvisibleCart(val) {
        // Dialog focus handling (WCAG 2.1.2 / 2.4.3): trap focus inside the drawer
        // while open, release it on close, then hand focus back to the opener.
        if (val) {
          slideCartOpener = document.activeElement
          document.addEventListener("keydown", slideCartTrap.keydown, true)
          document.addEventListener("focusin", slideCartTrap.focusin, true)
          this.$nextTick(() => {
            const root = slideCartRoot()
            if (root) root.focus()
            this.updateRecsEdges()
          })
        } else {
          this.hideCursor()
          document.removeEventListener("keydown", slideCartTrap.keydown, true)
          document.removeEventListener("focusin", slideCartTrap.focusin, true)
          if (slideCartOpener && slideCartOpener.isConnected) slideCartOpener.focus()
          slideCartOpener = null
        }
      },
    },
  }).mount("#cart-with-header")

  /* ------------------------------------------------- Broadcast integration */

  // Opening is wired up by the inline script in snippets/king-slide-cart.liquid,
  // which runs before Broadcast defines <cart-drawer>: the theme drawer's
  // openCartDrawer(), header cart links, theme:product:add and the Rebuy bundle
  // builder's rebuy:cart.add all end up in window.KingSlideCart.open().
  document.addEventListener("rebuy:cart.change", () => {
    if (!slideCartState.hidden) store.getCart()
  })

  window.store = store
  window.toggleCart = toggleCart
  window.toggleClose = toggleClose
  window.KingSlideCart = {
    open: () => toggleCart.showCart(),
    close: () => toggleClose.closeAll(),
    refresh: () => store.getCart(),
  }

  // Someone asked for the cart before this file had loaded, or the shopper
  // just switched country from inside the drawer.
  const early = window.__kingSlideCart
  const reopen = takeReopen()
  if ((early && early.pending) || reopen) {
    if (early) early.pending = false
    toggleCart.showCart()
  }
})()
